import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, VStack, HStack, Heading, Button, Table, Thead, Tbody, Tr, Th, Td,
    TableContainer, Input, InputGroup, InputLeftElement, Select, Card, CardBody,
    CardHeader, Badge, IconButton, Menu, MenuButton, MenuList, MenuItem,
    useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
    ModalBody, ModalCloseButton, FormControl, FormLabel, Spinner, Alert, AlertIcon,
    useColorModeValue, Flex, Text, useToast
} from '@chakra-ui/react';
import { MdSearch, MdAdd, MdEdit, MdDelete, MdMoreVert, MdRefresh, MdDynamicForm, MdAltRoute } from 'react-icons/md';
import ReactSelect from 'react-select';
import apiService from '../services/apiService';
import { ToastManager } from '../components/Common/ToastManager';
import { useSearchParams } from 'react-router-dom';
import SqlEditor from '../components/Common/SQLEditor';

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDateTime = d => {
    if (!d) return 'N/A';
    const date = new Date(d.includes('Z') ? d : d + 'Z');
    return isNaN(date) ? 'Invalid Date' : date.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

// ── Main component ────────────────────────────────────────────────────────────
function DynamicReportMappings({ userProfile }) {

    const [mappings,         setMappings]         = useState([]);
    const [filteredMappings, setFilteredMappings] = useState([]);
    const [clients,          setClients]          = useState([]);
    const [reports,          setReports]          = useState([]);
    const [loading,          setLoading]          = useState(true);
    const [searchTerm,       setSearchTerm]       = useState('');
    const [clientFilter,     setClientFilter]     = useState('all');
    const [reportFilter,     setReportFilter]     = useState('all');
    const [currentMapping,   setCurrentMapping]   = useState(null);
    const [isEditMode,       setIsEditMode]       = useState(false);

    const [isViewOpen, setIsViewOpen] = useState(false);
    const [viewTitle,  setViewTitle]  = useState('');
    const [viewSql,    setViewSql]    = useState('');
    const toast = useToast();

    const emptyForm = { reportId: '', clientIds: [] };
    const [formData,   setFormData]   = useState(emptyForm);
    const [formErrors, setFormErrors] = useState({});

    const { isOpen: isFormOpen,   onOpen: onFormOpen,   onClose: onFormClose   } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const [searchParams] = useSearchParams();

    // ── Theme ─────────────────────────────────────────────────────────────────
    const cardBg       = useColorModeValue('white',    'gray.800');
    const borderColor  = useColorModeValue('gray.200', 'gray.700');
    const textColor    = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const hoverBg      = useColorModeValue('gray.50',  'gray.700');
    const inputBg      = useColorModeValue('white',    'gray.700');
    const mutedColor   = useColorModeValue('gray.500', 'gray.400');
    const tableHeadBg  = useColorModeValue('gray.50',  'gray.900');

    const sqlEditorBg    = useColorModeValue('gray.900',  'gray.950');
    const sqlEditorBorder= useColorModeValue('gray.600',  'gray.700');
    const sqlEditorHdrBg = useColorModeValue('gray.800',  'gray.900');
    const sqlEditorHdrBd = useColorModeValue('gray.600',  'gray.700');
    const sqlTagBg       = useColorModeValue('blue.600',  'blue.500');
    const sqlCharColor   = useColorModeValue('gray.400',  'gray.500');
    const sqlPlaceholder = useColorModeValue('gray.600',  'gray.600');
    const viewSqlBodyBg  = useColorModeValue('gray.900',  'gray.950');
    const copyBtnBg      = useColorModeValue('gray.700',  'gray.600');
    const copyBtnHoverBg = useColorModeValue('gray.600',  'gray.500');

    const sqlTokens = {
        sqlEditorBg, sqlEditorBorder, sqlEditorHdrBg, sqlEditorHdrBd,
        sqlTagBg, sqlCharColor, sqlPlaceholder, viewSqlBodyBg, copyBtnBg, copyBtnHoverBg,
    };

    // react-select theme (CSS colors so it matches light/dark mode)
    const rsBg       = useColorModeValue('#ffffff', '#2D3748');
    const rsText     = useColorModeValue('#1A202C', '#F7FAFC');
    const rsBorder   = useColorModeValue('#E2E8F0', '#4A5568');
    const rsHover    = useColorModeValue('#EDF2F7', '#4A5568');
    const rsMultiBg  = useColorModeValue('#E2E8F0', '#4A5568');
    const selectStyles = useMemo(() => ({
        control: (base) => ({ ...base, backgroundColor: rsBg, borderColor: rsBorder, minHeight: '40px' }),
        menu:    (base) => ({ ...base, backgroundColor: rsBg, zIndex: 20 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option:  (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#3182CE' : state.isFocused ? rsHover : 'transparent',
            color: state.isSelected ? '#fff' : rsText,
        }),
        singleValue:     (base) => ({ ...base, color: rsText }),
        input:           (base) => ({ ...base, color: rsText }),
        multiValue:      (base) => ({ ...base, backgroundColor: rsMultiBg }),
        multiValueLabel: (base) => ({ ...base, color: rsText }),
    }), [rsBg, rsText, rsBorder, rsHover, rsMultiBg]);

    // ── View Query modal ──────────────────────────────────────────────────────
    const openViewQuery  = (title, sql) => { setViewTitle(title || 'Query'); setViewSql(sql || '-- No query found --'); setIsViewOpen(true); };
    const closeViewQuery = () => setIsViewOpen(false);

    // ── Available clients (exclude already-mapped ones for the selected report) ─
    const availableClients = useMemo(() => {
        if (!formData.reportId) return [];
        const mappedClientIds = new Set(
            mappings
                .filter(m => m.reportId?.toString() === formData.reportId)
                .map(m => m.clientId?.toString())
        );
        return clients.filter(c => !mappedClientIds.has(c.clientId?.toString()));
    }, [clients, mappings, formData.reportId]);

    const isAdminUser = () => userProfile &&
        (userProfile.role === 'admin' || userProfile.role === 'devops');

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const [ mappingsRes,clientsRes, reportsRes] = await Promise.all([
                apiService.getDynamicReportMappings(),
                apiService.getClientsList({ size: 1000 }),
                apiService.fetchDynamicReports(),
            ]);
            if (mappingsRes.success && mappingsRes.data)
                setMappings(Array.isArray(mappingsRes.data) ? mappingsRes.data : mappingsRes.data.content || []);
            if (clientsRes.success && clientsRes.data)
                setClients(clientsRes.data.content || []);
            if (reportsRes.success && reportsRes.data)
                setReports(Array.isArray(reportsRes.data) ? reportsRes.data : []);
        } catch {
            ToastManager.showError('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── Apply URL params (e.g. ?reportId=3 from DynamicReports page) ─────────
    useEffect(() => {
        const reportId = searchParams.get('reportId');
        if (reportId) setReportFilter(reportId.toString());
        const clientId = searchParams.get('clientId');
        if (clientId) setClientFilter(clientId.toString());
    }, [searchParams]);

    // ── Filter ────────────────────────────────────────────────────────────────
    useEffect(() => {
        let result = [...mappings];
        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            result = result.filter(m =>
                m.clientName?.toLowerCase().includes(q) ||
                m.reportName?.toLowerCase().includes(q)
            );
        }
        if (reportFilter !== 'all') result = result.filter(m => m.reportId?.toString() === reportFilter);
        if (clientFilter !== 'all') result = result.filter(m => m.clientId?.toString() === clientFilter);
        setFilteredMappings(result);
    }, [mappings, searchTerm, reportFilter, clientFilter]);

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = () => {
        const e = {};
        if (!formData.reportId) e.reportId = 'Please select a report.';
        if (!formData.clientIds || formData.clientIds.length === 0) e.clientIds = 'Please select at least one client.';
        return e;
    };

    // Client options for the multi-select (unmapped clients for the selected report)
    const clientOptions = useMemo(
        () => availableClients.map(c => ({ value: c.clientId, label: `${c.clientId} - ${c.clientName}` })),
        [availableClients]
    );

    // Resolve the controlled value for the multi-select, incl. the "all" pseudo-option
    const selectedClientOptions = useMemo(() => {
        if (formData.clientIds.includes('all')) {
            return [{ value: 'all', label: `All Available Clients (${availableClients.length})` }];
        }
        return clientOptions.filter(o => formData.clientIds.includes(o.value));
    }, [formData.clientIds, clientOptions, availableClients.length]);

    const handleClientSelectionChange = (selected) => {
        const values = (selected || []).map(o => o.value);
        // "All" is exclusive — selecting it replaces any individual selection
        setFormData(p => ({ ...p, clientIds: values.includes('all') ? ['all'] : values }));
        setFormErrors(p => ({ ...p, clientIds: '' }));
    };

    // ── Modal helpers ─────────────────────────────────────────────────────────
    const handleOpenCreate = () => {
        setIsEditMode(false); setCurrentMapping(null);
        setFormData(emptyForm); setFormErrors({});
        onFormOpen();
    };

    const handleOpenEdit = (mapping) => {
        setIsEditMode(true); setCurrentMapping(mapping);
        setFormData({
            reportId: mapping.reportId?.toString() || '',
            clientIds: mapping.clientId != null ? [mapping.clientId] : [],
        });
        setFormErrors({});
        onFormOpen();
    };

    const handleFormClose = () => {
        setFormData(emptyForm); setFormErrors({});
        setCurrentMapping(null); setIsEditMode(false);
        onFormClose();
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        const errors = validate();
        if (Object.keys(errors).length) { setFormErrors(errors); return; }
        try {
            const reportId = parseInt(formData.reportId, 10);

            if (isEditMode) {
                const res = await apiService.updateDynamicReportMapping(currentMapping.id, {
                    reportId,
                    clientId: parseInt(formData.clientIds[0], 10),
                    updatedBy: userProfile?.email,
                });
                if (res.success) {
                    ToastManager.showSuccess('Mapping updated successfully');
                    handleFormClose(); fetchAll();
                } else {
                    ToastManager.showError(res.message || 'Operation failed');
                }
                return;
            }

            // Create: resolve the selected clients (expand "all" to every available client)
            const clientIds = formData.clientIds.includes('all')
                ? availableClients.map(c => c.clientId)
                : formData.clientIds.map(id => parseInt(id, 10));

            if (clientIds.length === 0) {
                setFormErrors(p => ({ ...p, clientIds: 'Please select at least one client.' }));
                return;
            }

            // Single API call maps the report to all selected clients
            const res = await apiService.createDynamicReportMappingsBatch({
                reportId,
                clientIds,
                updatedBy: userProfile?.email,
            });

            if (res.success) {
                ToastManager.showSuccess(`${clientIds.length} mapping(s) created successfully`);
                handleFormClose(); fetchAll();
            } else {
                ToastManager.showError(res.message || 'Operation failed');
            }
        } catch {
            ToastManager.showError('Operation failed');
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleOpenDelete = (mapping) => { setCurrentMapping(mapping); onDeleteOpen(); };

    const handleConfirmDelete = async () => {
        try {
            const res = await apiService.deleteDynamicReportMapping(currentMapping.id);
            if (res.success) {
                ToastManager.showSuccess('Mapping deleted successfully');
                onDeleteClose(); setCurrentMapping(null); fetchAll();
            } else {
                ToastManager.showError(res.message || 'Failed to delete');
            }
        } catch {
            ToastManager.showError('Failed to delete mapping');
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box>
            <VStack spacing={6} align="stretch">

                {/* ── Header ── */}
                <Flex justify="space-between" align="center">
                    <Box>
                        <Heading size="lg" color={headingColor}>Dynamic Report Mappings</Heading>
                        <Text color={textColor} mt={1} fontSize="sm">
                            Map dynamic reports to clients for targeted data access
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        <Button leftIcon={<MdRefresh />} onClick={fetchAll} variant="outline" size="sm">Refresh</Button>
                        {isAdminUser() && (
                            <Button leftIcon={<MdAdd />} onClick={handleOpenCreate} colorScheme="blue" size="sm">
                                Create Mapping
                            </Button>
                        )}
                    </HStack>
                </Flex>

                {/* ── Filters ── */}
                <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardBody>
                        <Flex gap={4} align="center" wrap="wrap">
                            <InputGroup flex="1" minW="220px" maxW="380px">
                                <InputLeftElement pointerEvents="none"><MdSearch color={mutedColor} /></InputLeftElement>
                                <Input
                                    placeholder="Search by client or report name..."
                                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                    bg={inputBg} borderColor={borderColor} color={headingColor}
                                    _placeholder={{ color: mutedColor }}
                                />
                            </InputGroup>
                            <FormControl maxW="220px">
                                <Select value={reportFilter} onChange={e => setReportFilter(e.target.value)}
                                    bg={inputBg} borderColor={borderColor} color={headingColor}>
                                    <option value="all">All Reports</option>
                                    {reports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl maxW="220px">
                                <Select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
                                    bg={inputBg} borderColor={borderColor} color={headingColor}>
                                    <option value="all">All Clients</option>
                                    {clients.map(c => <option key={c.clientId} value={c.clientId}>{c.clientName}</option>)}
                                </Select>
                            </FormControl>
                            {(searchTerm || clientFilter !== 'all' || reportFilter !== 'all') && (
                                <Button size="md" variant="outline" colorScheme="purple"
                                    onClick={() => { setSearchTerm(''); setClientFilter('all'); setReportFilter('all'); }}>
                                    Reset Filters
                                </Button>
                            )}
                        </Flex>
                    </CardBody>
                </Card>

                {/* ── Table ── */}
                <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardHeader pb={0}>
                        <Flex justify="space-between" align="center">
                            <Heading size="md" color={headingColor}>Mappings</Heading>
                            <Badge colorScheme="blue" variant="subtle" fontSize="sm" px={3} py={1}>
                                {filteredMappings.length} record{filteredMappings.length !== 1 ? 's' : ''}
                            </Badge>
                        </Flex>
                    </CardHeader>
                    <CardBody p={0}>
                        {loading ? (
                            <Flex justify="center" py={16}>
                                <VStack spacing={4}>
                                    <Spinner size="lg" color="blue.500" thickness="3px" />
                                    <Text color={textColor}>Loading mappings...</Text>
                                </VStack>
                            </Flex>
                        ) : filteredMappings.length === 0 ? (
                            <Flex justify="center" py={16}>
                                <VStack spacing={3}>
                                    <Box color={mutedColor}><MdAltRoute size={40} /></Box>
                                    <Text color={mutedColor} fontSize="lg">No mappings found</Text>
                                    <Text color={mutedColor} fontSize="sm">
                                        {searchTerm || clientFilter !== 'all' || reportFilter !== 'all'
                                            ? 'Try adjusting your filters'
                                            : 'Create your first mapping to get started'}
                                    </Text>
                                </VStack>
                            </Flex>
                        ) : (
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead bg={tableHeadBg}>
                                        <Tr>
                                            <Th color={headingColor} py={4}>Client Name</Th>
                                            <Th color={headingColor} py={4}>Report Name</Th>
                                            <Th color={headingColor} py={4}>Last Updated</Th>
                                            <Th color={headingColor} py={4} w="80px">Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {filteredMappings.map((mapping, idx) => (
                                            <Tr key={mapping.id ?? idx} _hover={{ bg: hoverBg }}>

                                                {/* Client */}
                                                <Td py={4} borderColor={borderColor}>
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontSize="sm" fontWeight="semibold" color={headingColor}>{mapping.clientName}</Text>
                                                        <Text fontSize="xs" color={mutedColor}>ID: {mapping.clientId}</Text>
                                                    </VStack>
                                                </Td>

                                                {/* Report */}
                                                <Td py={4} borderColor={borderColor}>
                                                    <HStack spacing={2}>
                                                        <Box color="blue.500"><MdDynamicForm size={15} /></Box>
                                                        <VStack align="start" spacing={0}>
                                                            <Text fontSize="sm" fontWeight="medium" color={headingColor}>{mapping.reportName}</Text>
                                                            <Text fontSize="xs" color={mutedColor}>ID: {mapping.reportId}</Text>
                                                        </VStack>
                                                    </HStack>
                                                </Td>

                                                {/* Last Updated */}
                                                <Td py={4} borderColor={borderColor}>
                                                    <Text fontSize="xs" color={mutedColor}>{formatDateTime(mapping.updatedAt)}</Text>
                                                </Td>

                                                {/* Actions */}
                                                <Td py={4} borderColor={borderColor}>
                                                    <Menu>
                                                        <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm"
                                                            color={textColor} _hover={{ bg: hoverBg, color: headingColor }} />
                                                        <MenuList>
                                                            <MenuItem
                                                                icon={<MdDynamicForm />}
                                                                onClick={() => openViewQuery(
                                                                    `${mapping.clientName} • ${mapping.reportName}`,
                                                                    mapping.reportQuery || ''
                                                                )}
                                                            >
                                                                View Query
                                                            </MenuItem>
                                                            {isAdminUser() && (
                                                                <>
                                                                    {/* <MenuItem icon={<MdEdit />} onClick={() => handleOpenEdit(mapping)}>
                                                                        Edit
                                                                    </MenuItem> */}
                                                                    <MenuItem icon={<MdDelete />} color="red.500" onClick={() => handleOpenDelete(mapping)}>
                                                                        Delete
                                                                    </MenuItem>
                                                                </>
                                                            )}
                                                        </MenuList>
                                                    </Menu>
                                                </Td>

                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardBody>
                </Card>

                {/* ── Create / Edit Modal ── */}
                <Modal isOpen={isFormOpen} onClose={handleFormClose} size="md" isCentered>
                    <ModalOverlay />
                    <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
                            <HStack spacing={3}>
                                <Box color="blue.500"><MdAltRoute size={20} /></Box>
                                <Text>{isEditMode ? 'Edit Mapping' : 'Create Mapping'}</Text>
                            </HStack>
                        </ModalHeader>
                        <ModalCloseButton color={textColor} />

                        <ModalBody py={6}>
                            <VStack spacing={5} align="stretch">

                                {/* Report */}
                                <FormControl isRequired isInvalid={!!formErrors.reportId}>
                                    <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Dynamic Report</FormLabel>
                                    <Select
                                        placeholder="Select a report"
                                        value={formData.reportId}
                                        isDisabled={isEditMode}
                                        onChange={e => {
                                            setFormData(p => ({ ...p, reportId: e.target.value, clientIds: [] }));
                                            setFormErrors(p => ({ ...p, reportId: '', clientIds: '' }));
                                        }}
                                        bg={inputBg} borderColor={borderColor} color={headingColor}
                                    >
                                        {reports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                    </Select>
                                    {formErrors.reportId && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.reportId}</Text>}
                                </FormControl>

                                {/* Clients (multi-select) */}
                                <FormControl isRequired isInvalid={!!formErrors.clientIds}>
                                    <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">
                                        Clients
                                        {formData.reportId && !isEditMode && (
                                            <Text as="span" fontSize="xs" color={mutedColor} ml={2}>
                                                ({availableClients.length} available)
                                            </Text>
                                        )}
                                    </FormLabel>
                                    <ReactSelect
                                        isMulti
                                        isSearchable
                                        closeMenuOnSelect={false}
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        options={[
                                            { value: 'all', label: `All Available Clients (${availableClients.length})` },
                                            ...clientOptions,
                                        ]}
                                        value={selectedClientOptions}
                                        onChange={handleClientSelectionChange}
                                        styles={selectStyles}
                                        isDisabled={isEditMode || !formData.reportId || availableClients.length === 0}
                                        placeholder={
                                            !formData.reportId
                                                ? 'Select a report first'
                                                : availableClients.length === 0
                                                    ? 'All clients already mapped'
                                                    : 'Select one or more clients'
                                        }
                                    />
                                    {formData.reportId && !isEditMode && formData.clientIds.includes('all') && (
                                        <Text fontSize="xs" color="blue.500" mt={1}>
                                            ✓ All {availableClients.length} available client(s) will be mapped
                                        </Text>
                                    )}
                                    {formErrors.clientIds && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.clientIds}</Text>}
                                </FormControl>

                            </VStack>
                        </ModalBody>

                        <ModalFooter borderTop="1px" borderTopColor={borderColor}>
                            <Button colorScheme="blue" mr={3} onClick={handleSave}>
                                {isEditMode ? 'Update Mapping' : 'Create Mapping'}
                            </Button>
                            <Button variant="ghost" onClick={handleFormClose} color={textColor}>Cancel</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* ── Delete Modal ── */}
                <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="md" isCentered>
                    <ModalOverlay />
                    <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
                            <HStack spacing={3}>
                                <Box color="red.500"><MdDelete size={22} /></Box>
                                <Text>Confirm Deletion</Text>
                            </HStack>
                        </ModalHeader>
                        <ModalCloseButton color={textColor} />
                        <ModalBody py={6}>
                            <Alert status="warning" variant="left-accent" borderRadius="md" mb={4}>
                                <AlertIcon />This action cannot be undone.
                            </Alert>
                            <Text color={textColor} fontSize="sm">
                                Are you sure you want to delete the mapping between&nbsp;
                                <Text as="span" fontWeight="semibold" color={headingColor}>{currentMapping?.clientName}</Text>
                                &nbsp;and report&nbsp;
                                <Text as="span" fontWeight="semibold" color={headingColor}>{currentMapping?.reportName}</Text>?
                            </Text>
                        </ModalBody>
                        <ModalFooter borderTop="1px" borderTopColor={borderColor}>
                            <Button colorScheme="red" mr={3} leftIcon={<MdDelete />} onClick={handleConfirmDelete}>Delete Mapping</Button>
                            <Button variant="ghost" onClick={onDeleteClose} color={textColor}>Cancel</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* ── View Query Modal ── */}
                <Modal isOpen={isViewOpen} onClose={closeViewQuery} size="4xl" isCentered>
                    <ModalOverlay />
                    <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
                            <HStack spacing={3}>
                                <Box color="blue.400"><MdDynamicForm size={20} /></Box>
                                <Text>View Query</Text>
                            </HStack>
                            <Text fontSize="sm" color={textColor} mt={1}>{viewTitle}</Text>
                        </ModalHeader>
                        <ModalCloseButton color={textColor} />
                        <ModalBody p={0}>
                            <SqlEditor
                                value={viewSql}
                                onChange={() => {}}
                                isReadOnly
                                height="420px"
                                tokens={sqlTokens}
                                toast={toast}
                            />
                        </ModalBody>
                        <ModalFooter borderTop="1px" borderTopColor={borderColor}>
                            <Button onClick={closeViewQuery}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

            </VStack>
        </Box>
    );
}

export default DynamicReportMappings;